<template>
  <div class="organizationCard cloud-provider">
    <h3>{{ t("title") }}</h3>

    <div :class="['cloud-provider__row', 'cloud-provider__row--space-between']">
      <span class="cloud-provider__label">{{ t("inputs.provider") }}</span>

      <div class="cloud-provider__input-wrapper">
        <v-select
          :value="currentCloudProvider"
          :items="cloudProviderOptions"
          :disabled="!hasMoreThanOneOptionOfProvider"
          hide-details
        />
      </div>
    </div>

    <div :class="['cloud-provider__row', 'cloud-provider__row--space-between']">
      <span class="cloud-provider__label">{{ t("inputs.awsId") }}</span>

      <div class="cloud-provider__input-wrapper">
        <v-text-field v-model="awsId" readonly hide-details>
          <template v-slot:append>
            <div class="cloud-provider__row">
              <v-btn @click="copyAwsId" text icon>
                <v-icon>action_copy</v-icon>
              </v-btn>

              <app-tooltip :text="t('openAwsConsole')">
                <v-btn @click="openAwsConsole" text icon>
                  <img :src="VizNowAwsIcon" alt="aws logo" />
                </v-btn>
              </app-tooltip>
            </div>
          </template>
        </v-text-field>
      </div>
    </div>

    <div class="cloud-provider__column">
      <h4>{{ t("zones.title") }}</h4>

      <p class="cloud-provider__paragraph">
        {{ t("zones.subtitle") }}
      </p>

      <div class="cloud-provider__row cloud-provider__row--space-between">
        <span class="cloud-provider__label">{{ t("zones.numberOfZones") }}</span>

        <div :class="['cloud-provider__input-wrapper', 'cloud-provider__input-wrapper--mini']">
          <v-select
            v-model="cloudProviderMaxUsed"
            :items="cloudProviderCountOptions"
            rbt-target="useRegionCount"
            hide-details
          >
            <template v-slot:item="{ item }">
              <span :rbt-target="`option${item}`">{{ item }}</span>
            </template>
          </v-select>
        </div>
      </div>

      <draggable
        v-if="bestRegions.length"
        v-model="bestRegions"
        @start="dragging = true"
        @end="endDragging"
        :key="renderKey"
        tag="ul"
        ghost-class="cloud-provider__list-item--dragging"
        draggable=".cloud-provider__draggable"
        class="cloud-provider__list"
      >
        <li
          v-for="(region, index) in bestRegions"
          :class="[
            'cloud-provider__list-item',
            'cloud-provider__draggable',
            index + 1 <= cloudProviderMaxUsed && `cloud-provider__list-item--active`
          ]"
          :key="`${region.code}-${region.ping}`"
          :rbt-target="regionToCamelCased(region.code)"
        >
          <div class="cloud-provider__handler">
            <v-icon left>mdi-drag</v-icon>

            <span>
              {{ region.fullName }}
            </span>
          </div>

          <span :class="['cloud-provider__row', `cloud-provider__ping--${getPingSpeed(region.ping)}`]">
            <span :class="[`cloud-provider__graph`, `cloud-provider__graph--${getPingSpeed(region.ping)}`]">
              <span
                v-for="n in 5"
                :key="n"
                :class="['cloud-provider__graph-block', `cloud-provider__graph-block--${getPingSpeed(region.ping)}`]"
              />
            </span>

            <span class="cloud-provider__label-number">{{ region.ping }} ms</span>
          </span>
        </li>
      </draggable>

      <div class="cloud-provider__row">
        <v-btn @click="arrangeZones" rbt-target="arrangeZoneButton" large>{{ t("arrangeBestZones") }}</v-btn>

        <v-btn
          v-if="hasModificationToRegions"
          @click="saveCloudProviderOrder"
          :loading="loader.on"
          rbt-target="saveRegions"
          color="primary"
          large
          >{{ t("save") }}</v-btn
        >
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onBeforeUnmount } from "@vue/composition-api"
import { createTranslationModule } from "@/plugins/i18n"
import { getPingSpeed } from "@/config/cloud-provider-ping-speed"
import { useStore } from "@/composable/useStore"
import { CloudZone } from "@/types/core"
import { raiseError, raiseSuccess } from "@/utils/event-bus"
import { copyToClipboard } from "@/utils/ui"
import { URL_AWS_CONSOLE } from "@/config/aws-console"
import AppTooltip from "@/components/tooltip/TooltipRoot.vue"
import availableCloudProviders from "@/config/cloud-provider"
import Loader from "@/utils/loader"
import Utils from "@/utils/utils"
import Draggable from "vuedraggable"
import VizNowAwsIcon from "@/assets/viznow-aws-icon.svg"
import "./style/cloudProvider.scss"

export default defineComponent({
  name: "CloudProvider",
  props: {
    hasTabFocused: {
      type: Boolean,
      required: true
    }
  },
  setup(props) {
    const t = createTranslationModule("OrganizationPageV2.cloudProvider")
    const { organizationModule, adminModule, licenseModule } = useStore()
    const loader = Loader()
    const dragging = ref(false)
    const isMounted = ref(true)
    const renderKey = ref(0)
    const cloudProviderMaxUsed = ref(3)
    const bestRegions = ref<CloudZone[]>([])

    const cloudProviderOptions = computed(() => {
      return availableCloudProviders
    })

    const organization = computed(() => {
      return organizationModule.organizationDetail
    })

    const awsId = computed(() => {
      return organization.value.cloudProvider.accountId || ""
    })

    const hasModificationToRegions = computed(() => {
      const cuttingIndex = cloudProviderMaxUsed.value
      const compareBestRegions = bestRegions.value.slice(0, cuttingIndex)
      const compareCachedRegion = adminModule.cloudProviders.slice(0, cuttingIndex)

      return !compareBestRegions.every((region, index) => compareCachedRegion[index]?.name === region.name)
    })

    const currentCloudProvider = computed(() => {
      return organization.value.cloudProvider.name
    })

    const hasMoreThanOneOptionOfProvider = computed(() => {
      return cloudProviderOptions.value.some(option => {
        if (option.value !== currentCloudProvider.value) {
          return !option.disabled
        }
        return false
      })
    })

    const cloudProviderCountOptions = computed((): number[] => {
      const options: number[] = []
      const AWS_MAX_REGIONS_FALLBACK = 10
      const maxRegions = adminModule.cloudProviders.length || AWS_MAX_REGIONS_FALLBACK

      for (let i = 0; i < maxRegions; i++) {
        options.push(i + 1)
      }

      return options
    })

    const regionToCamelCased = (regionCode: string) => {
      return Utils.toCamel(regionCode.split("-").join(" ").trim())
    }

    const copyAwsId = async () => {
      try {
        await copyToClipboard(awsId.value)
        raiseSuccess({ text: t("prompt.copySuccess") })
      } catch {
        raiseError({ text: t("prompt.copyError") })
      }
    }

    const arrangeZones = () => {
      bestRegions.value.sort((a, b) => {
        return (a.ping ?? 0) - (b.ping ?? 0)
      })
    }

    const openAwsConsole = () => {
      window.open(URL_AWS_CONSOLE, "_blank")
    }

    const endDragging = () => {
      dragging.value = false
      renderKey.value++
    }

    const saveCloudProviderOrder = () => {
      loader.run(async () => {
        const newZones = bestRegions.value.slice(0, cloudProviderMaxUsed.value).map(zone => zone.code)

        await adminModule.updateOrganization({
          ...organization.value,
          cloudZones: newZones
        })

        await _updateBestRegionsStack()
      })
    }

    const _updateBestRegionsStack = async () => {
      await organizationModule.getOrganizationDetail(organization.value.id)
      _orderUsingOrganizationPreferences()
    }

    const _keepRegionsPingUpdated = async () => {
      const latency = Utils.seconds(2.5)

      if (isMounted.value) {
        setTimeout(async () => {
          if (!dragging.value && props.hasTabFocused) {
            await adminModule.refreshRegionPing(dragging.value)
          }
          _keepRegionsPingUpdated()
        }, latency)
      }
    }

    const _orderUsingOrganizationPreferences = () => {
      const organizationRegions = organization.value.cloudZones as string[]
      cloudProviderMaxUsed.value = organizationRegions.length

      bestRegions.value.sort(_sortLogic)
      adminModule.cloudProviders.sort(_sortLogic)
    }

    const _sortLogic = (zoneA: CloudZone, zoneB: CloudZone) => {
      const organizationRegions = organization.value.cloudZones as string[]
      const isInOrganizationA = organizationRegions.includes(zoneA.code)
      const isInOrganizationB = organizationRegions.includes(zoneB.code)
      const indexA = organizationRegions.indexOf(zoneA.code)
      const indexB = organizationRegions.indexOf(zoneB.code)

      if (isInOrganizationA && !isInOrganizationB) {
        return -1
      } else if (!isInOrganizationA && isInOrganizationB) {
        return 1
      } else if (isInOrganizationA && isInOrganizationB) {
        return indexA - indexB
      } else {
        return 0
      }
    }

    loader.run(async () => {
      await licenseModule.getRegions()
      await adminModule.getCloudZones(licenseModule.regions)
      await _updateBestRegionsStack()

      bestRegions.value = [...adminModule.cloudProviders]
      _orderUsingOrganizationPreferences()
      _keepRegionsPingUpdated()
    })

    onBeforeUnmount(() => {
      isMounted.value = false
    })

    return {
      openAwsConsole,
      awsId,
      copyAwsId,
      VizNowAwsIcon,
      regionToCamelCased,
      hasModificationToRegions,
      arrangeZones,
      bestRegions,
      cloudProviderCountOptions,
      cloudProviderMaxUsed,
      cloudProviderOptions,
      currentCloudProvider,
      dragging,
      endDragging,
      getPingSpeed,
      hasMoreThanOneOptionOfProvider,
      loader,
      renderKey,
      saveCloudProviderOrder,
      t
    }
  },
  components: {
    AppTooltip,
    Draggable
  }
})
</script>
